import React, { Component } from "react";
import { BrowserRouter as Router, Route, Switch, Redirect, useLocation } from "react-router-dom";
import styled from "styled-components";
import { UserContext } from "./UserContext";
import { PolicyType } from "src/constants/policyType";
import LeftNavigation from "./components/LeftNavigation";
import MatrixSessionProvider from "src/scenes/Chat/components/MatrixSessionProvider";
import CallWindowPage from "src/scenes/Chat/components/call/CallWindowPage";
import GuestMeetingPage from "src/scenes/Meet";
import QuoteCategorySelection from "../EmployeeList/QuoteCategorySelection";
import axios from "axios";
import {
    FlexContainer, Toast, Modal,
    LicenseRenewalContainer, ResetPasswordContainer, SetupPasswordContainer, SignUpContainer,
    Header, AuthorizedRoute, LottieLoadingIndicator,
    AcceptPoliciesModal, RevokedPermissionsModal, PageContainer
} from "src/components";
import { DashboardThemeProvider, useDashboardTheme } from "src/components/DashboardTheme";
import {
    LicenseRenewal, ResetPassword,
    Login, SignUp, SetupPassword, ForgotAccessCode, ForgotPassword,
    Subscription, SubscriptionRenewal, Customise, EmployeeList, ClientLicense, Noticeboard, MyQuotes,
    Chat, Directory, Playlists, Survey, Perk, Donation, DigitalMemberCard, Statistics, Setting,
    Advertising, BodyAndMind, Unsubscribe,
    EmailCampaigns, Calendar
} from "src/scenes";
import * as PERMISSIONS from "src/constants/permissions";
import { getLowestPermissionRoute } from "src/constants/permissions";
import { IsFeatureReady } from "src/constants/features";
import { UserKeys, SubscriptionMode } from "src/constants/userDetails";
import { fullMatrixLogout } from "../Chat/utils";
import { clearNonMatrixLocalStorage } from "../Chat/utils/matrixStorage";
import { setGlobalAccountOwnerEmployeeName, setGlobalCustomNames } from "src/utils/helpers";
import { reportErrorToSentry } from "src/utils/sentry";
import InitialPasswordContainer from "src/components/InitialPasswordContainer";
import InitialPassword from "../InitialPassword";
import { FEATURE_FLAGS, hasFeature } from "src/constants/FeatureFlag";
import { ErrorCategory, installAxiosResponseInterceptor, setPolicyAcceptanceHandler } from "src/api/axiosInterceptor";
import CheckedInEmailVerificationPage from "src/scenes/CheckedInEmailVerification/CheckedInEmailVerificationPage";
import CheckedInAuth from "src/scenes/CheckedInAuth/CheckedInAuth";
import CheckedInSite from "src/components/CheckedIn/CheckedInSite";
import { isCheckedInClient } from "src/utils/checkedInUser";

installAxiosResponseInterceptor();

const getEnvironment = () => {
    switch (window.location.hostname) {
        case "localhost": return ENVIRONMENT.DEV;
        case "staging.memotivationapp.com": return ENVIRONMENT.STAGING;
        case "prototype.memotivationapp.com": return ENVIRONMENT.PROTOTYPE;
        case "business.memotivationapp.com": return ENVIRONMENT.PRODUCTION;
        default:
            console.log("[ERROR] Running on unrecognised host: " + window.location.hostname);
            return null;
    }
};

export const ENVIRONMENT = {
    DEV: "dev",
    STAGING: "staging",
    PROTOTYPE: "prototype",
    PRODUCTION: "production",
    get: getEnvironment
};

export const LoginType = {
    CompanyAccount: "companyAccount",
    CompanyUser: "companyUser",
    LicenseUuid: "licenseUuid",
    ShortTermToken: "shortTermToken"
};

/** @type {string | null} */
export let API_REGION = null;

/** @type {string | null} */
export let API_URL = null;

/** @type {string | null} */
export let SERVERLESS_IMAGE_HANDLER_URL = null;

/** @type {string | null} */
export let CONTENT_S3_BUCKET = null;

/** @type {string} */
export const GLOBAL_SERVICE_URL = (() => {
    switch (ENVIRONMENT.get()) {
        case ENVIRONMENT.DEV: return "http://127.0.0.1:3001";
        case ENVIRONMENT.STAGING: return "https://global.staging.memotivationapp.com";
        case ENVIRONMENT.PROTOTYPE: return "https://global.staging.memotivationapp.com";
        case ENVIRONMENT.PRODUCTION: return "https://global.memotivationapp.com";
        default: return "";
    }
})();

/**
 * @type {{
 *   delegateNewUser: (string) => Promise<string>,
 *   locateUser: (string, string) => Promise<string>,
 *   loadRegionData: (string) => Promise<void>,
 *   setRegionData: (string) => void,
 *   clearRegionalData: () => void
 * }}
 */
export const RegionalApiManager = {
    async delegateNewUser(iso3166Code) {
        const response = await axios.post(`${GLOBAL_SERVICE_URL}/delegate`, { iso3166Code });
        return response.data;
    },
    async locateUser(loginType, identifier) {
        const response = await axios.post(`${GLOBAL_SERVICE_URL}/locate`, { loginType, identifier });
        return response.data;
    },
    async loadRegionData(userRegion, onFail) {
        const response = await axios.get(`${GLOBAL_SERVICE_URL}/regions`);
        const match = response.data.find(region => region.region === userRegion);
        if (!match) {
            Toast.error("Your region could not be found.");
            onFail();
            return;
        }
        this.setRegionData(match);
    },
    setRegionData(data) {
        const { region, apiUrl, imageServiceUrl, contentBucket } = data;
        API_REGION = region;
        API_URL = apiUrl;
        SERVERLESS_IMAGE_HANDLER_URL = imageServiceUrl;
        CONTENT_S3_BUCKET = contentBucket;
    },
    clearRegionalData() {
        API_REGION = null;
        API_URL = null;
        SERVERLESS_IMAGE_HANDLER_URL = null;
        CONTENT_S3_BUCKET = null;
    }
};

const LoggedInRightContainer = styled(FlexContainer)`
    background: var(--calendar-bg, ${p => p.backgroundColor});
    color: var(--calendar-text, #171A22);
    flex-grow: 1;
    height: auto;
    width: auto;
    overflow-x: hidden;
`;

function ThemedLoggedInRightContainer(props) {
    const { themeMode } = useDashboardTheme();
    return <LoggedInRightContainer data-theme={themeMode} {...props} />;
}

export const customStyles = {
    content: {
        top: "50%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        marginRight: "-50%",
        width: "40%",
        backgroundColor: "#292C33",
        color: "#F4F4F4",
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        transform: "translate(-50%, -50%)"
    }
};

export const StorageKey = {
    TOKEN: "token",
    COMPANY_NAME: "companyName",
    COMPANY_ABN: "abn",
    UUID: "uuid",
    FIRST_NAME: "firstName",
    LAST_NAME: "lastName",
    IS_CUSTOMIZE_COMPLETE: "isCustomizeComplete",
    IS_ACTIVE: "isActive",
    EMAIL: "email",
    COUNTRY_CODE: "countryCode",
    ENABLED_CLIENT_LICENSE: "enabledClientLicense",
    PERMISSIONS: "permissions",
    ACCESS_CODE: "accessCode",
    COMPANY_LOGO_URL: "companyLogoUrl",
    PROFILE_PICTURE_URL: "profilePictureUrl",
    IS_MATRIX_REGISTRATION_COMPLETE: "isMatrixRegistrationComplete"
};

const PublicPasswordPaths = ["initialPassword", "setPassword"];
export const currentPathSegment = () => window.location.pathname.split("/")[1];
export const currentPathIsPublicPassword = () => PublicPasswordPaths.includes(currentPathSegment());
export const currentPathIsPublicUnauthenticated = () => (
    currentPathIsPublicPassword() || currentPathSegment() === "checkedIn"
);
export const currentPathIsPublicMeeting = () => currentPathSegment() === "meet";

const ExternalAppCallbacks = {
    showAcceptPermissionModal: (policyKind) => {
        console.error("Unset callback");
    }
};

class App extends Component {
    constructor(props) {
        super(props);
        ExternalAppCallbacks.showAcceptPermissionModal = (policyType) => {
            if (!Object.values(PolicyType).includes(policyType)) {
                Toast.error("An unknown policy must be accepted.");
                return;
            }
            this.setState({
                policyToAccept: policyType
            });
        };
        setPolicyAcceptanceHandler(ExternalAppCallbacks.showAcceptPermissionModal);
    }

    state = {
        isLoadingFonts: true,
        user: null,
        contentLeftMargin: window.innerWidth <= 1280 ? 75 : 270,
        currentWidth: null,
        isUserAuthenticated: false,
        isUserDataLoaded: false,
        isRestoringCheckedInSession: window.location.pathname === "/checkedIn/landing"
            && Boolean(localStorage.getItem(UserKeys.TOKEN) && localStorage.getItem(UserKeys.REGION)),
        componentBackground: "#EAEDF4",
        policyToAccept: undefined,
        pendingChatBackupPassphrase: null
    };

    resizeTimeoutId = undefined;

    resizeHandler = (element) => {
        if (isCheckedInClient(this.state.user)) {
            return;
        }
        const newContentLeftMargin = window.innerWidth <= 1280 ? 75 : 270;
        element.style.padding = `0 0 0 ${newContentLeftMargin}px`;
    };

    hidePolicyAcceptanceModal() {
        this.setState({
            policyToAccept: undefined
        });
    }

    handleResize = () => {
        const container = document.getElementById("content-container");
        if (!container) return;

        clearTimeout(this.resizeTimeoutId);
        this.resizeTimeoutId = setTimeout(() => this.resizeHandler(container), 100);
    };

    redirectToLoginIfNoToken = () => {
        const excludedPaths = [
            "login",
            "resetPassword",
            "setPassword",
            "licenseRenewal",
            "initialPassword",
            "unsubscribePage",
            "meet"
        ];
        if (!excludedPaths.includes(currentPathSegment())) {
            window.location.href = "/login";
        }
    };

    /**
     * This prevents access when the 'resetPassword' path clears local storage.
     * Opening 'resetPassword' in a new tab clears local storage for all 'memotivation' tabs,
     * but does not affect component state and 'userContext' in other tabs.
     * Thus, other tabs remain authenticated and authorized.
     * Boot out all 'memotivation' tabs when local storage is cleared
     */
    handleStorageChange = (e) => {
        if (!localStorage.length || (this.state.user && !localStorage.getItem(UserKeys.TOKEN))) {
            const destination = isCheckedInClient(this.state.user)
                ? "/checkedIn/landing" : "/login";
            this.setState({
                user: null,
                pendingChatBackupPassphrase: null
            });
            window.location.href = destination;
        }
    };

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
        window.removeEventListener("storage", this.handleStorageChange);
        clearTimeout(this.resizeTimeoutId);
    }

    async componentDidMount() {
        Toast.init();
        await this.initUser();
        window.addEventListener("resize", this.handleResize);
        window.addEventListener("storage", this.handleStorageChange);
        this.handleSmallDeviceWarning();
    }

    handleSmallDeviceWarning = () => {
        // A warning will be displayed whenever the screen is refreshed while a user is
        // logged in and using a device with a smaller than most laptops (1280px).
        const condition = window.innerWidth < 1280 && this.state.user !== null
            && !isCheckedInClient(this.state.user);
        if (condition) {
            Modal.open("SmallDevice",
                <div>
                    The Me administration page was not developed for a small screen, so
                    we recommend a laptop or desktop be used for the best user experience.
                </div>
            );
        }
    };

    initUser = async () => {
        const token = localStorage.getItem(UserKeys.TOKEN);
        const region = localStorage.getItem(UserKeys.REGION);
        const restoreCheckedInSession = window.location.pathname === "/checkedIn/landing" && Boolean(token && region);
        if (currentPathIsPublicUnauthenticated() && !restoreCheckedInSession) {
            return;
        }

        if (!token || !region) {
            return this.redirectToLoginIfNoToken();
        }
        // Prevent showing the non-authorised components resulting to <NoMatch /> being rendered when refreshing.
        this.setState({ isUserAuthenticated: true });
        try {
            await RegionalApiManager.loadRegionData(region, () => this.loggedOut());
            const userDetails = await this.getUserDetails(token);
            if (userDetails) {
                this.updateUserStateFromResponse(region, userDetails);
            }
        } catch (err) {
            console.error("Unable to initialize user.", err);
        } finally {
            this.setState({ isRestoringCheckedInSession: false });
        }
    };

    /**
     * This will retrieve all user's details and permissions
     * @return {Object|null}
     */
    getUserDetails = async (token) => {
        const isLicenseRoute = window.location.pathname.includes("licenseRenewal");
        try {
            const response = await axios.get(`${API_URL}/company/userDetails`, { headers: { Authorization: `Bearer ${token}` } });
            return response.data;
        } catch (error) {
            if (!isLicenseRoute) {
                console.log(error);
                Toast.error("Unable to retrieve your information. Please log in again.");

                // The Axios interceptor (see axiosInterceptor.ts) triggers automatic logout on irrecoverable auth
                // failure, policy acceptance popups, and more.
                // For other cases, we really need to know what happened in this specific place since it leads to
                // unusable UI.
                if (error.getErrorCategory() === ErrorCategory.UnknownFailure) {
                    const message = error.getApiErrorDetails().message || "Unknown error";
                    reportErrorToSentry("getUserDetails", message);
                }
            }
            return null;
        }
    };

    /**
     * Return whether a paid subscription has existed before, based on what the current subscription looks like
     */
    subscriptionWasEverPaid = subscription => {
        return subscription && (subscription.previousUuid !== null || subscription.periodsPaid !== 0);
    };

    updateUserStateFromResponse = (region, data) => {
        setGlobalCustomNames(data.customUserGroupClients, data.customUserGroupEmployees);
        const permissions = JSON.parse(data.roleAndPermissions.permissionIds);
        const accountOwnerEmployeeDetails = permissions.includes(PERMISSIONS.ACCOUNT_OWNER_PERMISSION)
            ? data.employeeDetails
            : null;
        setGlobalAccountOwnerEmployeeName(
            accountOwnerEmployeeDetails?.firstName,
            accountOwnerEmployeeDetails?.lastName
        );

        this.setState({
            user: {
                [UserKeys.UUID]: data.uuid,
                [UserKeys.EMPLOYEE_UUID]: data.employeeDetails?.uuid || data.uuid,
                [UserKeys.REGION]: region,
                [UserKeys.EMAIL]: data.email,
                [UserKeys.ACCESS_CODE]: data.accessCode,
                [UserKeys.FIRST_NAME]: data.firstName,
                [UserKeys.LAST_NAME]: data.lastName,
                [UserKeys.PROFILE_PICTURE_URL]: data.profilePictureUrl,
                [UserKeys.DEPARTMENT]: data.department,
                [UserKeys.EMPLOYMENT_POSITION]: data.employmentPosition,
                [UserKeys.PHONE_NUMBER]: data.phoneNumber,
                [UserKeys.BIRTH_DATE]: data.birthDate,
                [UserKeys.IS_ACTIVE]: data.isActive,
                [UserKeys.SUBSCRIPTION_MODE]: data.subscriptionMode || SubscriptionMode.NONE,
                [UserKeys.SUBSCRIPTION_WAS_EVER_PAID]: this.subscriptionWasEverPaid(data.currentSubscription),
                [UserKeys.SUBSCRIPTION_IS_VALID]: Boolean(data.currentSubscription) && !data.currentSubscription.isExpired,
                [UserKeys.IS_CLIENT]: data.isClient,
                [UserKeys.PERMISSIONS]: permissions,
                [UserKeys.FEATURE_FLAGS]: data.roleAndPermissions.featureFlags,
                [UserKeys.TOKEN]: data.token,
                [UserKeys.LEADERSHIP_ROLES]: data.leadershipRoles,
                // company details below
                [UserKeys.COMPANY_NAME]: data.companyName,
                [UserKeys.APP_VARIANT]: data.appVariant,
                [UserKeys.COMPANY_ABN]: data.abn,
                [UserKeys.IS_CUSTOMIZE_COMPLETE]: data.isCustomizeComplete,
                [UserKeys.COUNTRY_CODE]: data.countryCode,
                [UserKeys.ENABLED_CLIENT_LICENSE]: data.enabledClientLicense,
                [UserKeys.COMPANY_LOGO_URL]: data.companyLogoUrl,
                [UserKeys.COMPANY_LOGO_UPDATED_AT]: data.companyLogoUpdatedAt,
                [UserKeys.COMPANY_BACKGROUND_COLOR]: data.backgroundColor,
                [UserKeys.COMPANY_PRIMARY_COLOR]: data.primaryColor,
                [UserKeys.MATRIX_USER_ID]: data.matrixUserId,
                [UserKeys.CHAT_PRESENCE_OVERRIDE]: data.chatPresenceOverride,
                [UserKeys.CUSTOM_NAME_DIRECTORY]: data.customDirectoryName,
                [UserKeys.CUSTOM_NAME_MY_QUOTES]: data.customMyQuotesName,
                [UserKeys.CUSTOM_NAME_BODY_AND_MIND]: data.customBodyAndMindName,
                [UserKeys.CUSTOM_NAME_USER_GROUPS_CLIENTS]: data.customUserGroupClients,
                [UserKeys.CUSTOM_NAME_USER_GROUPS_EMPLOYEES]: data.customUserGroupEmployees,
                [UserKeys.CUSTOM_NAME_SALES_AND_PROMOTIONS]: data.customSalesAndPromotionsName,
                [UserKeys.CUSTOM_NAME_DIGITAL_MEMBER_CARDS]: data.customMembershipCardName,
                [UserKeys.LICENSE_TIER]: data.licenseTier,
                [UserKeys.REQUIRED_DOCUMENTS]: data.requiredDocuments || [],
                appFns: {
                    updateUserContextProperties: this.updateUserContextProperties,
                    reinitialiseUser: this.initUser
                }
            },
            isUserAuthenticated: true,
            isUserDataLoaded: true
        });
    };

    /** @param {object} properties - key-value pairs */
    updateUserContextProperties = (properties) => {
        this.setState({
            user: { ...this.state.user, ...properties }
        });
    };

    loggedInSuccess = (region, data, options) => {
        localStorage.setItem(UserKeys.TOKEN, data.token);
        localStorage.setItem(UserKeys.REGION, region);
        this.setState({
            pendingChatBackupPassphrase: options?.chatBackupPassphrase || null
        });
        this.updateUserStateFromResponse(region, data);
    };

    clearPendingChatBackupPassphrase = () => {
        this.setState({
            pendingChatBackupPassphrase: null
        });
    };

    loggedOut = async () => {
        this.setState({
            user: null,
            isUserAuthenticated: false,
            isUserDataLoaded: false,
            pendingChatBackupPassphrase: null
        });
        await fullMatrixLogout();
        clearNonMatrixLocalStorage();
    };

    /**
     * TODO: might be possible to use the updateUserContextProperties instead */
    setEnabledClientLicenseFlag = (isEnabled) => {
        this.setState({
            user: {
                ...this.state.user,
                enabledClientLicense: isEnabled
            }
        });

        localStorage.setItem(StorageKey.ENABLED_CLIENT_LICENSE, isEnabled);
    };

    isChatRendered = (value) => {
        const background = value ? "#FFF" : "#EAEDF4";
        this.setState({
            componentBackground: background
        });
    };

    updateUserPresenceState = (onlineStatus) => {
        this.setState({
            user: {
                ...this.state.user,
                [UserKeys.CHAT_PRESENCE_OVERRIDE]: onlineStatus
            }
        });
    };

    render() {
        const {
            user, contentLeftMargin, isUserAuthenticated, isUserDataLoaded, componentBackground, isRestoringCheckedInSession
        } = this.state;
        const token = localStorage.getItem(StorageKey.TOKEN);
        const canViewFeature = (featureFlag) => {
            if (!featureFlag) {
                return true;
            }

            if (Array.isArray(featureFlag)) {
                return featureFlag.some((flag) => hasFeature(user, flag));
            }

            return hasFeature(user, featureFlag);
        };

        return (
            <UserContext.Provider value={user}>
                <Router>
                    <Switch>
                        {IsFeatureReady.publicMeetings() && <Route exact path="/meet/:meetingToken" render={props => (
                            isUserAuthenticated && isUserDataLoaded ? (
                                <MatrixSessionProvider
                                    user={user}
                                    updateUserPresenceState={this.updateUserPresenceState}
                                    loginPassphrase={this.state.pendingChatBackupPassphrase}
                                    onLoginPassphraseConsumed={this.clearPendingChatBackupPassphrase}
                                >
                                    <GuestMeetingPage {...props} />
                                </MatrixSessionProvider>
                            ) : <GuestMeetingPage {...props} />
                        )} />}
                        <Route exact path="/call-window" component={CallWindowPage} />
                        <Route path="/unsubscribePage">
                            <Unsubscribe />
                        </Route>
                        <Route path="/licenseRenewal/:referralCode/:key" render={(props) => (
                            <LicenseRenewalContainer {...props}>
                                <LicenseRenewal {...props} />
                            </LicenseRenewalContainer>
                        )} />

                        <Route path="/initialPassword/:shortTermToken" render={(props) => (
                            <InitialPasswordContainer {...props}>
                                <InitialPassword {...props} />
                            </InitialPasswordContainer>
                        )} />
                        <Route path="/setPassword/:shortTermToken" render={(props) => (
                            <SetupPasswordContainer {...props}>
                                <SetupPassword {...props} />
                            </SetupPasswordContainer>
                        )} />

                        <Route
                            exact
                            path="/checkedIn/verifyEmail/:token"
                            component={CheckedInEmailVerificationPage}
                        />
                        <Route exact path="/checkedIn/landing">
                            {user ? <Redirect to={getLowestPermissionRoute(user.permissions, user.appVariant, user.isClient)} />
                                : isRestoringCheckedInSession ? <LottieLoadingIndicator />
                                    : <CheckedInAuth login={this.loggedInSuccess} />}
                        </Route>
                        <Route exact path={["/checkedIn/login", "/checkedIn/confirm"]}>
                            <CheckedInAuth login={this.loggedInSuccess} />
                        </Route>

                        <Route path="/forgotPassword/:userType" render={(props) => (
                            <SignUpContainer>
                                <ForgotPassword {...props} logout={this.loggedOut} />
                            </SignUpContainer>
                        )} />
                        <Route path="/resetPassword/:portal/:token/:email?" render={(props) => (
                            <ResetPasswordContainer {...props}>
                                <ResetPassword {...props} logout={this.loggedOut} />
                            </ResetPasswordContainer>
                        )} />
                        <Route path="/forgotAccessCode" render={(props) => (
                            <SignUpContainer>
                                <ForgotAccessCode {...props} logout={this.loggedOut} />
                            </SignUpContainer>
                        )} />

                        <Route exact path="/">
                            {
                                token === null
                                    ? <Redirect to="/login" />
                                    : user
                                        ? <Redirect to={getLowestPermissionRoute(user.permissions, user.appVariant, user.isClient)} />
                                        : <Redirect to="/active" />
                            }
                        </Route>

                        {(!isUserAuthenticated && !isUserDataLoaded) &&
                            <Switch>
                                <Route path="/signUp" render={(props) => (
                                    <SignUpContainer>
                                        <SignUp {...props} signUpComplete={this.loggedInSuccess} />
                                    </SignUpContainer>
                                )} />
                                <Route path="/login" render={(props) => (
                                    <SignUpContainer>
                                        <Login {...props} login={this.loggedInSuccess} />
                                    </SignUpContainer>
                                )} />
                                <Route path="*">
                                    <SignUpContainer>
                                        <NoMatch />
                                    </SignUpContainer>
                                </Route>
                            </Switch>
                        }
                        {isUserAuthenticated && isUserDataLoaded ? isCheckedInClient(user) ? (
                            <CheckedInSite user={user} logout={this.loggedOut} />
                        ) :
                            <MatrixSessionProvider
                                user={user}
                                updateUserPresenceState={this.updateUserPresenceState}
                                loginPassphrase={this.state.pendingChatBackupPassphrase}
                                onLoginPassphraseConsumed={this.clearPendingChatBackupPassphrase}
                            >
                                <DashboardThemeProvider>
                                    <Header />
                                    <FlexContainer direction="row" height="auto" minHeight="100vh" overflowY="auto" padding="64px 0 0 0" style={{ position: "relative", zIndex: 1 }}>
                                        <LeftNavigation
                                            logout={this.loggedOut}
                                            user={user}
                                        />
                                        <ThemedLoggedInRightContainer backgroundColor={componentBackground}>
                                            <FlexContainer
                                                id="content-container"
                                                padding={`0 0 0 ${contentLeftMargin}px`}
                                                style={{ transition: "0.2s all ease-in-out", flexGrow: "1" }}
                                            >
                                                <Switch>
                                                    <Route exact path="/">
                                                        <Redirect to={getLowestPermissionRoute(user.permissions, user.appVariant, user.isClient)} />
                                                    </Route>
                                                    <AuthorizedRoute path="/active"
                                                        component={Subscription}
                                                        allowAuthenticated
                                                    />
                                                    <AuthorizedRoute path="/renewal"
                                                        component={SubscriptionRenewal}
                                                        allowAuthenticated
                                                    />
                                                    {canViewFeature(FEATURE_FLAGS.MY_ME_APP) &&
                                                        <AuthorizedRoute path="/customise"
                                                            component={Customise}
                                                            allowed={[PERMISSIONS.MY_ME_APP]}
                                                        />
                                                    }
                                                    {canViewFeature(FEATURE_FLAGS.USER_LISTS) &&
                                                        <AuthorizedRoute path="/employList"
                                                            component={EmployeeList} user={user} isClient={false}
                                                            allowed={[PERMISSIONS.USER_LISTS]}
                                                        />
                                                    }
                                                    {/* <AuthorizedRoute path="/invite" component={EmployeeList} user={user} isClient={true}
                                                        allowed={[PERMISSIONS.WEB_DASHBOARD_ACCESS]}
                                                    /> */}
                                                    <AuthorizedRoute path="/clientLicense"
                                                        component={ClientLicense} user={user}
                                                        setEnabledClientLicenseFlag={this.setEnabledClientLicenseFlag}
                                                        allowed={[PERMISSIONS.SALES_AND_PROMOTIONS]}
                                                    />
                                                    {canViewFeature(FEATURE_FLAGS.NOTICEBOARD) &&
                                                        <AuthorizedRoute path="/noticeboard"
                                                            component={Noticeboard} user={user}
                                                            allowAuthenticated
                                                        />
                                                    }

                                                    {canViewFeature(FEATURE_FLAGS.BUSINESS_CHAT) && (
                                                        <AuthorizedRoute path="/chats"
                                                            component={Chat} user={user}
                                                            allowAuthenticated
                                                            isChatRendered={this.isChatRendered}
                                                            updateUserPresenceState={this.updateUserPresenceState}
                                                            getUserDetails={this.getUserDetails}
                                                        />
                                                    )}

                                                    {canViewFeature(FEATURE_FLAGS.BODY_AND_MIND) &&
                                                        <AuthorizedRoute path="/body-and-mind"
                                                            component={BodyAndMind} user={user}
                                                            allowed={[PERMISSIONS.BODY_AND_MIND]}
                                                        />
                                                    }
                                                    {canViewFeature(FEATURE_FLAGS.QUOTES) &&
                                                        <AuthorizedRoute path="/myquotes"
                                                            component={MyQuotes} user={user}
                                                            allowed={[PERMISSIONS.MY_QUOTE]}
                                                        />
                                                    }
                                                    {canViewFeature(FEATURE_FLAGS.PLAYLISTS) &&
                                                        <AuthorizedRoute
                                                            path="/playlists"
                                                            component={Playlists}
                                                            user={user}
                                                            allowed={[PERMISSIONS.CREATE_BUSINESS_PLAYLISTS]}
                                                        />
                                                    }
                                                    {canViewFeature([FEATURE_FLAGS.SURVEYS, FEATURE_FLAGS.POLLS]) &&
                                                        <AuthorizedRoute path="/survey"
                                                            component={Survey} user={user}
                                                            allowed={[PERMISSIONS.SURVEYS, PERMISSIONS.POLLS]}
                                                            allowAny
                                                        />
                                                    }
                                                    {canViewFeature(FEATURE_FLAGS.ADVERTISING) &&
                                                        <AuthorizedRoute path="/advertising"
                                                            component={Advertising} user={user}
                                                            allowed={[PERMISSIONS.ADVERTISING]}
                                                        />
                                                    }
                                                    {canViewFeature(FEATURE_FLAGS.PERKS) &&
                                                        <AuthorizedRoute path="/perks"
                                                            component={Perk} user={user}
                                                            allowed={[PERMISSIONS.PERKS]}
                                                        />
                                                    }
                                                    {canViewFeature(FEATURE_FLAGS.DONATIONS) &&
                                                        <AuthorizedRoute path="/donations"
                                                            component={Donation} user={user}
                                                            allowed={[PERMISSIONS.DONATIONS]}
                                                        />
                                                    }
                                                    {canViewFeature(FEATURE_FLAGS.EMAIL_CAMPAIGNS) &&
                                                        <AuthorizedRoute path="/email-campaigns"
                                                            component={EmailCampaigns} user={user}
                                                            allowed={[PERMISSIONS.EMAIL_CAMPAIGNS]}
                                                        />
                                                    }
                                                    {canViewFeature(FEATURE_FLAGS.BUSINESS_DIRECTORY) &&
                                                        <AuthorizedRoute path="/directory"
                                                            component={Directory} user={user}
                                                            allowed={[PERMISSIONS.BUSINESS_DIRECTORY]}
                                                        />
                                                    }

                                                    {canViewFeature(FEATURE_FLAGS.DIGITAL_MEMBER_CARDS) &&
                                                        <AuthorizedRoute path="/digitalMemberCards"
                                                            component={DigitalMemberCard} user={user}
                                                            allowed={[PERMISSIONS.DIGITAL_MEMBER_CARDS]}
                                                        />
                                                    }

                                                    <AuthorizedRoute path="/calendar"
                                                        component={Calendar} user={user}
                                                        allowAuthenticated
                                                    />

                                                    {canViewFeature(FEATURE_FLAGS.STATISTICS) &&
                                                        <AuthorizedRoute path="/statistics"
                                                            component={Statistics} user={user}
                                                            allowed={[PERMISSIONS.STATISTICS]}
                                                        />
                                                    }
                                                    <AuthorizedRoute path="/setting"
                                                        component={Setting}
                                                        setEnabledClientLicenseFlag={this.setEnabledClientLicenseFlag}
                                                        allowAuthenticated
                                                    />

                                                    {IsFeatureReady.clientQuoteCategorySelection() &&
                                                        <AuthorizedRoute path="/quoteSelection"
                                                            component={QuoteCategorySelection} isClient={true} user={user}
                                                            allowed={[PERMISSIONS.USER_LISTS]}
                                                        />
                                                    }

                                                    {/** because we added the "NoMatch" component, the disadvantage is having the T6647 error
                                                     * Just adding the routes can prevent this issue.  both this page displays blank, when user is already logged-in.
                                                     * */}

                                                    <Route path="/signUp">
                                                        <Redirect to={getLowestPermissionRoute(user.permissions, user.appVariant, user.isClient)} />
                                                    </Route>

                                                    <Route path="/login">
                                                        <Redirect to={getLowestPermissionRoute(user.permissions, user.appVariant, user.isClient)} />
                                                    </Route>

                                                    <Route path="*">
                                                        <NoMatch />
                                                    </Route>
                                                </Switch>
                                            </FlexContainer>
                                        </ThemedLoggedInRightContainer>
                                    </FlexContainer>
                                </DashboardThemeProvider>
                            </MatrixSessionProvider>
                            :
                            <LottieLoadingIndicator />
                        }
                    </Switch>
                </Router>
                <RevokedPermissionsModal refreshPermissions={() => this.getUserDetails()} />
                { this.state.policyToAccept &&
                    <AcceptPoliciesModal
                        appVariant={this.state.user?.[UserKeys.APP_VARIANT]}
                        policyToAccept={this.state.policyToAccept}
                        hideModal={() => this.hidePolicyAcceptanceModal()}
                    />
                }
            </UserContext.Provider>
        );
    }
}

function NoMatch() {
    let location = useLocation();

    return (
        <div style={{ display: "flex", height: "80vh", alignItems: "center" }}>
            <PageContainer>
                <h3>
                    No match for <code>{location.pathname}</code>
                </h3>
            </PageContainer>
        </div>
    );
}

export default App;
